import { useState, useCallback } from "react";
import { FiCheckCircle } from "react-icons/fi";
import { Checkbox } from "@chakra-ui/react";
import { showToast } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useInitialEvaluationContext } from "../../../../hooks/InitialEvaluationContext";
import { useMyScores } from "../../../../hooks/usePanelScoringQueries";
import { saveOrSubmitScores } from "../../../../api/panel-scoring-api";
import { SupplierResponseTable } from "../SupplierResponseTable";
import { ScoringModal } from "../ScoringModal";
import { ReadOnlyScoreTable } from "./member/ReadOnlyScoreTable";

interface LeadScoringSectionProps {
	rfxId: number;
	evalId: number;
	isLeadScoring: boolean;
	isLeadSubmitted: boolean;
	onAddClarification?: (inviteId: number) => void;
	onSubmitted?: () => void;
}

export const LeadScoringSection: React.FC<LeadScoringSectionProps> = ({
	rfxId,
	evalId,
	isLeadScoring,
	isLeadSubmitted,
	onAddClarification,
	onSubmitted,
}) => {
	const [isExpanded, setIsExpanded] = useState(!isLeadSubmitted);
	const [selectedInviteId, setSelectedInviteId] = useState<number | null>(null);
	const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
	const [confirmChecked, setConfirmChecked] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		suppliers,
		isLoading: isSuppliersLoading,
		error,
		allSuppliersScored,
		refreshSupplier,
	} = useInitialEvaluationContext();

	const handleSelectSupplier = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleCloseModal = useCallback(() => {
		if (selectedInviteId !== null) {
			refreshSupplier(selectedInviteId);
		}
		setSelectedInviteId(null);
	}, [selectedInviteId, refreshSupplier]);

	const handleSupplierChange = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleScoringComplete = useCallback(() => {
		if (selectedInviteId !== null) {
			refreshSupplier(selectedInviteId);
		}
	}, [selectedInviteId, refreshSupplier]);

	const handleSubmitScores = useCallback(() => {
		if (!allSuppliersScored) {
			showToast(
				"All suppliers must be fully scored before submitting.",
				"Evaluation Incomplete",
				false
			);
			return;
		}
		setShowSubmitConfirm(true);
	}, [allSuppliersScored]);

	const handleConfirmSubmit = useCallback(async () => {
		setIsSubmitting(true);
		try {
			await saveOrSubmitScores(rfxId, evalId, { action: "submit", scores: [] });
			showToast("Your scores have been submitted successfully.", "Scores Submitted", true);
			setShowSubmitConfirm(false);
			setConfirmChecked(false);
			setIsExpanded(false);
			onSubmitted?.();
		} catch {
			showToast("Failed to submit scores. Please try again.", "Error", false);
		} finally {
			setIsSubmitting(false);
		}
	}, [rfxId, evalId, onSubmitted]);

	const handleCancelSubmit = useCallback(() => {
		setShowSubmitConfirm(false);
		setConfirmChecked(false);
	}, []);

	if (!isLeadScoring) {
		return null;
	}

	const { data: myScoresData, isLoading: isLoadingMyScores } = useMyScores(
		isLeadSubmitted ? rfxId : undefined,
		isLeadSubmitted ? evalId : undefined
	);

	if (isLeadSubmitted) {
		return (
			<div data-testid="lead-scoring-section" className="space-y-4">
				<div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
					<FiCheckCircle size={20} className="shrink-0 text-green-600" aria-hidden="true" />
					<div>
						<p className="text-sm font-medium text-green-800">
							Your scores have been submitted
						</p>
						<p className="mt-1 text-xs text-green-700">
							Your evaluation is complete. View your submitted scores below.
						</p>
					</div>
				</div>
				{isLoadingMyScores ? (
					<div className="space-y-2">
						{[1, 2].map((i) => (
							<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
						))}
					</div>
				) : (
					<ReadOnlyScoreTable
						suppliers={myScoresData?.suppliers ?? []}
						rfxId={rfxId}
						onAddClarification={onAddClarification}
					/>
				)}
			</div>
		);
	}

	return (
		<div data-testid="lead-scoring-section">
			<div className="mt-3 space-y-4">
				{error && (
					<p className="text-sm text-red-600" data-testid="lead-scoring-error">
						{error}
					</p>
				)}

				<SupplierResponseTable
					suppliers={suppliers}
					isLoading={isSuppliersLoading}
					rfxId={rfxId}
					onSelectSupplier={handleSelectSupplier}
				/>

				<div className="flex justify-end">
					<Button
						btntype="primary"
						onClick={handleSubmitScores}
						disabled={!allSuppliersScored}
						data-testid="lead-submit-scores-btn"
					>
						Submit Scores
					</Button>
				</div>
			</div>

			{selectedInviteId !== null && (
				<ScoringModal
					isOpen={selectedInviteId !== null}
					rfxId={rfxId}
					suppliers={suppliers}
					selectedInviteId={selectedInviteId}
					onClose={handleCloseModal}
					onSupplierChange={handleSupplierChange}
					onScoringComplete={handleScoringComplete}
				/>
			)}

			{showSubmitConfirm && (
				<Modal
					isOpen={showSubmitConfirm}
					onClose={handleCancelSubmit}
					title="Submit Your Scores"
					size="md"
					iconType="warning"
					modalType="standard"
					closeOnEsc
					closeOnOverlayClick={false}
					primaryButton={{
						text: isSubmitting ? "Submitting..." : "Submit",
						onClick: handleConfirmSubmit,
						disabled: !confirmChecked || isSubmitting,
						width: "full"
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: handleCancelSubmit,
						width: "full"
					}}
				>
					<div className="space-y-4">
						<p className="text-sm text-gray-600 text-center">
							Once submitted, your evaluation scores and comments cannot be edited.
							Please ensure all scores are accurate before proceeding.
						</p>
						<label
							className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
							data-testid="lead-submit-confirm-checkbox"
						>
							<Checkbox
								isChecked={confirmChecked}
								onChange={(e) => setConfirmChecked(e.target.checked)}
								colorScheme="blue"
								mt={0.5}
							/>
							<span className="text-sm text-gray-700">
								I confirm that I have completed all evaluations to the best of my knowledge
								and understand that scores cannot be changed after submission.
							</span>
						</label>
					</div>
				</Modal>
			)}
		</div>
	);
};
